/**
 * FilterBar — responsive strip of search/filter controls.
 *
 * On desktop:  fields sit in a horizontal row.
 * On mobile:   each field takes full width, stacked vertically.
 *
 * Usage:
 *   <FilterBar
 *     filters={[
 *       { key: 'search',  label: 'Search',  type: 'text',   placeholder: 'Name…' },
 *       { key: 'status',  label: 'Status',  type: 'select', options: ['ACTIVE','INACTIVE'] },
 *       { key: 'from',    label: 'From',    type: 'date'  },
 *     ]}
 *     values={filterState}
 *     onChange={(key, val) => setFilterState(prev => ({ ...prev, [key]: val }))}
 *     onClear={() => setFilterState(defaultState)}
 *   />
 */
import React from 'react';

const inputBase = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #ECE3E7',
  borderRadius: 10,
  fontSize: 13,
  color: '#2b1b22',
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color .15s, box-shadow .15s',
};

const labelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: '#8b7681',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

const focusOn  = e => {
  e.target.style.borderColor = '#7B1E3C';
  e.target.style.boxShadow = '0 0 0 3px rgba(123,30,60,.18)';
};
const focusOff = e => {
  e.target.style.borderColor = '#ECE3E7';
  e.target.style.boxShadow = 'none';
};

export default function FilterBar({ filters = [], values = {}, onChange, onClear }) {
  const hasAny = filters.some(f => values[f.key]);

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #ECE3E7',
      borderRadius: 16,
      boxShadow: '0 1px 2px rgba(13,27,46,.04), 0 10px 30px -16px rgba(13,27,46,.10)',
      padding: 14,
      marginBottom: 18,
    }}>
      {/* Filter fields grid — 2 cols on mobile, auto on desktop */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 10,
        alignItems: 'end',
      }}>
        {filters.map(f => (
          <div key={f.key}>
            <label style={labelStyle}>{f.label}</label>
            {f.type === 'select' ? (
              <select
                style={inputBase}
                value={values[f.key] || ''}
                onChange={e => onChange(f.key, e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
              >
                <option value="">{f.allLabel || 'All'}</option>
                {(f.options || []).map(o => (
                  <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
                    {typeof o === 'string' ? o.replace(/_/g, ' ') : o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                style={inputBase}
                type={f.type || 'text'}
                placeholder={f.placeholder || ''}
                value={values[f.key] || ''}
                onChange={e => onChange(f.key, e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
              />
            )}
          </div>
        ))}

        {/* Clear button sits in the grid as the last cell */}
        {hasAny && (
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={onClear}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#fff',
                border: '1px solid #ECE3E7',
                borderRadius: 10,
                fontSize: 12,
                color: '#8b7681',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              ✕ Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
